import React, { useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { useAreas } from '../../../../core/api';
import clsx from 'clsx';
import { RemoteImage } from '../../../../core/components/RemoteImage';

interface AreaSelectorProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

export const AreaSelector: React.FC<AreaSelectorProps> = ({ selectedIds, onChange }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const { data: areas = [], isLoading } = useAreas({});
    const areaList = (areas as any[]);

    const toggleArea = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(aid => aid !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const filtered = areaList.filter((a: any) =>
        (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.slug || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = [...filtered].sort((a: any, b: any) => {
        const aSelected = selectedIds.includes(a.id);
        const bSelected = selectedIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return (a.name || '').localeCompare(b.name || '');
    });

    if (isLoading) return <div className="p-4 text-center text-slate-500 text-sm">Loading areas...</div>;

    return (
        <div className="space-y-3">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    placeholder="Search locations..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                />
            </div>

            <div className="border rounded-lg max-h-56 overflow-y-auto">
                {sorted.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">No locations found.</div>
                ) : (
                    <div className="divide-y">
                        {sorted.map((area: any) => {
                            const isSelected = selectedIds.includes(area.id);
                            return (
                                <div
                                    key={area.id}
                                    onClick={() => toggleArea(area.id)}
                                    className={clsx(
                                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors",
                                        isSelected && "bg-primary/5"
                                    )}
                                >
                                    <div className={clsx(
                                        "w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                        isSelected ? "bg-primary border-primary text-white" : "border-slate-300 bg-white"
                                    )}>
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">
                                            {area.name || area.slug}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">{area.slug}</p>
                                    </div>
                                    {area.image && (
                                        <RemoteImage
                                            src={area.image}
                                            alt=""
                                            className="w-10 h-10 rounded-full object-cover bg-slate-100 flex-shrink-0"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selectedIds.map(id => {
                        const area = areaList.find((a: any) => a.id === id);
                        if (!area) return null;
                        return (
                            <div key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 border rounded-full text-xs">
                                <span className="max-w-[100px] truncate">{area.name || area.slug}</span>
                                <button onClick={() => toggleArea(id)} className="hover:text-red-500 p-0.5">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedIds.length === 0 && (
                <p className="text-xs text-slate-500 italic">No locations selected — all areas will be shown.</p>
            )}
        </div>
    );
};
